"use client";

import { c as _c } from "react/compiler-runtime";
import { formatForDisplay, useHotkey, useHotkeyRegistrations } from "@tanstack/react-hotkeys";
import { useId, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@domainstack/ui/dialog";
import { Kbd, KbdGroup } from "@domainstack/ui/kbd";
import { cn } from "@domainstack/ui/utils";
type ShortcutItem = {
  id: string;
  name: string;
  /** Anything `formatForDisplay` accepts, e.g. "Mod+K" or a lone "Shift". */
  hotkey: string;
  /** A pointer action that follows the keys, e.g. "click". */
  gesture?: string;
};

/** Known groups, in display order; unknown groups follow in registration order. */
const GROUP_ORDER = ["Global", "Dashboard", "Selection"];

/**
 * Pointer gestures can't be registered as hotkeys, so they are listed here and
 * shown only while their group has live registrations.
 */
const POINTER_SHORTCUTS: Array<ShortcutItem & {
  group: string;
}> = [{
  id: "dashboard-range-select",
  group: "Selection",
  name: "Select a range",
  hotkey: "Shift",
  gesture: "click"
}];

/** One keycap per key, as symbols on macOS (⌘ K) and labels elsewhere (Ctrl K). */
function ShortcutKeys(t0) {
  const $ = _c(18);
  const {
    hotkey,
    gesture
  } = t0;
  let T0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== gesture || $[1] !== hotkey) {
    const keys = formatForDisplay(hotkey, {
      parts: true
    });
    let t4;
    if ($[6] !== hotkey) {
      t4 = formatForDisplay(hotkey, {
        parts: true,
        useSymbols: false
      }).join(" ");
      $[6] = hotkey;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    const spoken = t4;
    T0 = KbdGroup;
    t1 = "shrink-0";
    const t5 = gesture ? `${spoken} ${gesture}` : spoken;
    if ($[8] !== t5) {
      t2 = <span className="sr-only">{t5}</span>;
      $[8] = t5;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t3 = keys.map(_temp);
    $[0] = gesture;
    $[1] = hotkey;
    $[2] = T0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    T0 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[10] !== gesture) {
    t4 = gesture ? <span aria-hidden={true} className="text-xs text-muted-foreground">{gesture}</span> : null;
    $[10] = gesture;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== T0 || $[13] !== t1 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4) {
    t5 = <T0 className={t1}>{t2}{t3}{t4}</T0>;
    $[12] = T0;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  return t5;
}
function _temp(key) {
  return <Kbd key={key} aria-hidden={true} className="border bg-muted/80 px-1.5 tabular-nums">{key}</Kbd>;
}
function ShortcutSection(t0) {
  const $ = _c(11);
  const {
    title,
    items
  } = t0;
  const headingId = useId();
  let t1;
  if ($[0] !== headingId || $[1] !== title) {
    t1 = <h3 id={headingId} className="mb-1 text-xs font-medium text-muted-foreground">{title}</h3>;
    $[0] = headingId;
    $[1] = title;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== items) {
    t2 = items.map(_temp2);
    $[3] = items;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = <ul className="divide-y divide-border/60">{t2}</ul>;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== headingId || $[8] !== t1 || $[9] !== t3) {
    t4 = <section aria-labelledby={headingId} className="mb-5 break-inside-avoid last:mb-0">{t1}{t3}</section>;
    $[7] = headingId;
    $[8] = t1;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}

/**
 * Reads the live hotkey registry, so every shortcut with `meta.name` shows up
 * while the component that registers it is mounted. Rendered only inside the
 * open dialog, because the registry also updates each time a hotkey fires.
 */
function _temp2(item) {
  return <li key={item.id} className="flex min-h-9 items-center justify-between gap-4 py-1.5"><span className="min-w-0 truncate text-sm">{item.name}</span><ShortcutKeys hotkey={item.hotkey} gesture={item.gesture} /></li>;
}
function ShortcutList() {
  const $ = _c(19);
  const {
    hotkeys
  } = useHotkeyRegistrations();
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== hotkeys) {
    const groups = new Map();
    const seen = new Set();
    for (const registration of hotkeys) {
      const {
        meta
      } = registration.options;
      if (!meta?.name || seen.has(registration.hotkey)) {
        continue;
      }
      seen.add(registration.hotkey);
      const group = meta.group ?? "Global";
      const items = groups.get(group) ?? [];
      items.push({
        id: registration.id,
        name: meta.name,
        hotkey: registration.hotkey
      });
      groups.set(group, items);
    }
    for (const {
      group: group_0,
      ...item
    } of POINTER_SHORTCUTS) {
      groups.get(group_0)?.push(item);
    }
    const rank = _temp3;
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = (t7, t8) => {
        const [a] = t7;
        const [b] = t8;
        return rank(a) - rank(b);
      };
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    const sections = [...groups].sort(t6);
    const multiColumn = sections.length > 1;
    t3 = 0;
    t4 = "Shortcuts";
    t5 = "-mx-5 min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset";
    t0 = multiColumn || undefined;
    const t7 = multiColumn && "sm:columns-2 sm:gap-8";
    if ($[8] !== t7) {
      t1 = cn(t7);
      $[8] = t7;
      $[9] = t1;
    } else {
      t1 = $[9];
    }
    t2 = sections.map(_temp4);
    $[0] = hotkeys;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  let t6;
  if ($[10] !== t0 || $[11] !== t1 || $[12] !== t2) {
    t6 = <div data-multi-column={t0} className={t1}>{t2}</div>;
    $[10] = t0;
    $[11] = t1;
    $[12] = t2;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5 || $[17] !== t6) {
    t7 = <div tabIndex={t3} aria-label={t4} className={t5}>{t6}</div>;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
function _temp4(t0) {
  const [title, items_0] = t0;
  return <ShortcutSection key={title} title={title} items={items_0} />;
}
function _temp3(group_1) {
  const index = GROUP_ORDER.indexOf(group_1);
  return index === -1 ? GROUP_ORDER.length : index;
}
export function KeyboardShortcutsDialog() {
  const $ = _c(5);
  const [open, setOpen] = useState(false);
  const popupRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => setOpen(true);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      ignoreInputs: true,
      requireReset: true,
      meta: {
        name: "Show keyboard shortcuts",
        description: "Open this list of shortcuts",
        group: "Global"
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  useHotkey("?", t0, t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DialogContent ref={popupRef} initialFocus={popupRef} className="sm:max-w-md sm:has-data-multi-column:max-w-2xl"><DialogHeader><DialogTitle>Keyboard Shortcuts</DialogTitle><DialogDescription className="sr-only">Shortcuts available on this page</DialogDescription></DialogHeader><ShortcutList /></DialogContent>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== open) {
    t3 = <Dialog open={open} onOpenChange={setOpen}>{t2}</Dialog>;
    $[3] = open;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}